import { Alert, Progress, Space, Tag, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';

import {
    isRunActive,
    runAlertType,
    runCountEntries,
    runFailureKey,
    runHeadline,
    runPercent,
    runStartedText,
    type EInvoiceRun,
} from '../../domain/eInvoice';

/**
 * The progress bar, always shown: moving while a run sends, greyed out at where the last run
 * stopped (or empty, before the first) otherwise.
 */
function RunProgress({ percent, active }: Readonly<{ percent: number; active: boolean }>) {
    const { token } = theme.useToken();

    return (
        <Progress
            percent={percent}
            status={active ? 'active' : 'normal'}
            strokeColor={active ? undefined : token.colorTextDisabled}
            data-testid="e-invoice-run-progress"
            data-active={String(active)}
        />
    );
}

/**
 * The latest e-invoice run, for every user (plan §6.10): a progress bar and the invoice being sent
 * while it goes, how it ended once it has, who started it and how the invoices ended.
 */
export default function EInvoiceRunStatusCard({
    run,
    loaded,
}: Readonly<{ run: EInvoiceRun | null; loaded: boolean }>) {
    const { t } = useTranslation();

    if (!loaded) {
        return null;
    }

    if (run === null) {
        return (
            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                data-testid="e-invoice-run-status"
                message={t('eInvoice.runStatus.none')}
                description={<RunProgress percent={0} active={false} />}
            />
        );
    }

    const active = isRunActive(run);
    const headline = runHeadline(run);
    const started = runStartedText(run);
    const counts = runCountEntries(run);

    return (
        <Alert
            type={runAlertType(run)}
            showIcon
            style={{ marginBottom: 12 }}
            data-testid="e-invoice-run-status"
            data-status={run.status}
            message={
                <span data-testid="e-invoice-run-headline">{t(headline.key, headline.params)}</span>
            }
            description={
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <RunProgress percent={runPercent(run)} active={active} />
                    {active && run.currentInvoiceNumber !== null && (
                        <Typography.Text data-testid="e-invoice-run-current">
                            {t('eInvoice.runStatus.current', {
                                number: run.currentInvoiceNumber,
                            })}
                        </Typography.Text>
                    )}
                    {run.status === 'FAILED' && (
                        <Typography.Text type="danger" data-testid="e-invoice-run-failure">
                            {t(runFailureKey(run))}
                        </Typography.Text>
                    )}
                    <Typography.Text type="secondary" data-testid="e-invoice-run-started">
                        {t(started.key, started.params)}
                    </Typography.Text>
                    {counts.length > 0 && (
                        <Space wrap size={4}>
                            {counts.map(entry => (
                                <Tag
                                    key={entry.key}
                                    data-testid={`e-invoice-run-count-${entry.key}`}
                                >
                                    {t(`eInvoice.runStatus.counts.${entry.key}`, {
                                        count: entry.count,
                                    })}
                                </Tag>
                            ))}
                        </Space>
                    )}
                </Space>
            }
        />
    );
}
